import React, { useEffect } from 'react';
import { useQueryLoader } from 'react-relay';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { Periscope } from '@atlassian/jira-periscope/src/ui/index.tsx';
import { SCREEN, ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import INSIGHTS_DEVELOPMENT_QUERY, {
	type insightsDevelopmentQuery,
} from '@atlassian/jira-relay/src/__generated__/insightsDevelopmentQuery.graphql';
import QUERY, {
	type uiPeriscopeQuery,
} from '@atlassian/jira-relay/src/__generated__/uiPeriscopeQuery.graphql';
import { useResource } from '@atlassian/jira-router';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater';
import { AsyncNoSoftwareLicense } from '@atlassian/jira-spa-apps-common/src/error-screens/no-software-license/async';
import { AuthenticationCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication';
import { checkLicense } from '@atlassian/jira-spa-apps-common/src/interceptors/license';
import { MarkProductStart } from '@atlassian/jira-spa-performance-breakdown/src/utils/mark-product-start/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import {
	ERROR_REPORTING_ID_NG,
	ERROR_REPORTING_PACKAGE_NAME,
	ERROR_REPORTING_ID_CLASSIC,
} from '../../common/constants';
import {
	getThirtyDaysAgoIsoString,
	getThisWeekEndIsoString,
	getThisWeekStartIsoString,
} from '../../common/utils.tsx';

type Props = {
	projectAriString: string | null;
	cloudId: string;
};

export const PeriscopeEntry = ({ projectAriString, cloudId }: Props) => {
	const { data: projectData } = useResource(projectContextResource);
	const tenantContext = useTenantContext();

	const hasSoftwareLicense = checkLicense(tenantContext, 'software');
	const [workSuggestionsQueryRef, loadWorkSuggestionsQuery] =
		useQueryLoader<uiPeriscopeQuery>(QUERY);
	const [insightsDevelopmentQueryRef, loadInsightsDevelopmentQuery] =
		useQueryLoader<insightsDevelopmentQuery>(INSIGHTS_DEVELOPMENT_QUERY);

	useEffect(() => {
		if (hasSoftwareLicense && projectAriString) {
			loadWorkSuggestionsQuery({
				cloudId,
				projectAris: [projectAriString],
				sprintAutoDiscoveryLimit: 10,
				isAutoDevJobsSuggestionEnabled: fg('jira-work-suggestions-autodev-jobs-suggestions'),
				isPullRequestSuggestionsEnabled: fg('jira-periscope-pr-comment-suggestions'),
				isCompassSuggestionsEnabled: fg('jira_periscope_compass_announcements'),
			});

			if (projectData?.projectId && fg('jira_periscope_insights_panel')) {
				const currentDate = new Date();
				loadInsightsDevelopmentQuery({
					cloudId,
					projectAri: projectAriString,
					projectId: String(projectData?.projectId),
					prsCreatedAfter: getThirtyDaysAgoIsoString(currentDate),
					startFromInclusive: getThisWeekStartIsoString(currentDate),
					endAtExclusive: getThisWeekEndIsoString(currentDate),
					timezoneId:
						Intl.DateTimeFormat().resolvedOptions().timeZone !== 'Etc/Unknown'
							? Intl.DateTimeFormat().resolvedOptions().timeZone
							: '',
				});
			}
		}
	}, [
		loadWorkSuggestionsQuery,
		loadInsightsDevelopmentQuery,
		projectData,
		projectAriString,
		cloudId,
		hasSoftwareLicense,
	]);

	const errorReportIdPrefix =
		projectData && projectData.simplified ? ERROR_REPORTING_ID_NG : ERROR_REPORTING_ID_CLASSIC;
	return (
		<>
			<MarkProductStart />
			<AuthenticationCheck>
				{hasSoftwareLicense ? (
					<JSErrorBoundary
						id={`${errorReportIdPrefix}-periscope-page`}
						packageName={ERROR_REPORTING_PACKAGE_NAME}
						sendToPrivacyUnsafeSplunk
						teamName="nova"
						onError={(
							_,
							// error: Error
						) => {
							// experienceTracker.fail({
							// 	name: VIEW_PERISCOPE_PAGE_EXPERIENCE_NAME,
							// 	error,
							// });
						}}
					>
						<SubProductUpdater subProduct="software" />
						<ContextualAnalyticsData sourceName="DevelopmentMain" sourceType={SCREEN}>
							{workSuggestionsQueryRef && (
								<Periscope
									queryReference={workSuggestionsQueryRef}
									insightsDevelopmentQueryRef={insightsDevelopmentQueryRef}
								/>
							)}
						</ContextualAnalyticsData>
					</JSErrorBoundary>
				) : (
					<>
						<AsyncNoSoftwareLicense />
					</>
				)}
			</AuthenticationCheck>
		</>
	);
};
